// Johku Hub's app scans with the phone's own camera (ML Kit on Android, AVFoundation on the iPhone),
// drawn over the page where the reader's preview is: faster and surer than reading the web camera's
// frames, with the light where the phone has one. The app gives Johku's pages a JohkuHubScanner
// (Android) or webkit.messageHandlers.johkuHubScanner (iPhone) and answers with 'johkuhubscanner'
// events on the window. The same as Hub's own reader (johku-hub: app/lib/scanner.ts).

function bridge () {
  if (typeof window === 'undefined') {
    return null
  }
  if (window.JohkuHubScanner && typeof window.JohkuHubScanner.postMessage === 'function') {
    return message => window.JohkuHubScanner.postMessage(JSON.stringify(message))
  }
  const handler = window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.johkuHubScanner
  if (handler) {
    return message => handler.postMessage(JSON.stringify(message))
  }
  return null
}

export function hubScannerAvailable () {
  return !!bridge()
}

// The app buzzes for a scan's result: success, warning or error
export function hubFeedback (kind) {
  const post = bridge()
  if (!post) {
    return false
  }
  post({ op: 'feedback', kind })
  return true
}

function isClipping (element) {
  const style = window.getComputedStyle(element)
  return style.overflowX !== 'visible' || style.overflowY !== 'visible'
}

// Where the preview is now, cut to what its scrolling ancestors and the window show; not visible
// when something covers it (a ticket's result, a dialog) or the page is hidden
function layoutOf (element) {
  const box = element.getBoundingClientRect()
  let left = Math.max(0, box.left)
  let top = Math.max(0, box.top)
  let right = Math.min(window.innerWidth, box.right)
  let bottom = Math.min(window.innerHeight, box.bottom)
  for (let parent = element.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
    if (isClipping(parent)) {
      const clip = parent.getBoundingClientRect()
      left = Math.max(left, clip.left)
      top = Math.max(top, clip.top)
      right = Math.min(right, clip.right)
      bottom = Math.min(bottom, clip.bottom)
    }
  }
  const rect = { x: Math.round(left), y: Math.round(top), width: Math.max(0, Math.round(right - left)), height: Math.max(0, Math.round(bottom - top)) }
  let visible = !document.hidden && element.isConnected && rect.width > 0 && rect.height > 0
  if (visible) {
    const hit = document.elementFromPoint(left + (right - left) / 2, top + (bottom - top) / 2)
    visible = !!hit && (hit === element || element.contains(hit))
  }
  return { rect, viewport: window.innerWidth, visible }
}

export class HubQrScanner {
  constructor ({ element, onScan, onError, onCapabilities }) {
    this.element = element
    this.onScan = onScan
    this.onError = onError
    this.onCapabilities = onCapabilities
    this.post = bridge()
    this.frame = null
    this.sent = ''
    this.stopped = false
    this.onEvent = this.onEvent.bind(this)
  }

  start () {
    if (!this.post) {
      return Promise.reject(new Error('Johku Hub app not available'))
    }
    this.stopped = false
    window.addEventListener('johkuhubscanner', this.onEvent)
    return new Promise((resolve, reject) => {
      this.pending = { resolve, reject }
      this.post({ op: 'start', ...layoutOf(this.element) })
      this.follow()
    })
  }

  // The preview follows the element: scrolling, a resized window, a result covering it
  follow () {
    if (this.stopped) {
      return
    }
    const layout = layoutOf(this.element)
    const key = JSON.stringify(layout)
    if (key !== this.sent) {
      this.sent = key
      this.post({ op: 'layout', ...layout })
    }
    this.frame = requestAnimationFrame(() => this.follow())
  }

  onEvent (event) {
    const detail = (event && event.detail) || {}
    if (this.stopped) {
      return
    }
    if (detail.type === 'code' && detail.text && this.onScan) {
      this.onScan(detail.text)
    } else if (detail.type === 'started') {
      if (this.onCapabilities) {
        this.onCapabilities({ torch: !!detail.torch, cameras: detail.cameras || 0 })
      }
      this.settle()
    } else if (detail.type === 'error' || detail.type === 'stopped') {
      const error = new Error('Camera error')
      error.name = detail.name || 'Error'
      if (this.pending) {
        this.settle(error)
      } else if (this.onError) {
        this.onError(error)
      }
    }
  }

  // The camera starts once the preview shows: start() resolves then, or rejects with the error
  settle (error) {
    if (!this.pending) {
      return
    }
    const pending = this.pending
    this.pending = null
    if (error) {
      pending.reject(error)
    } else {
      pending.resolve()
    }
  }

  nextCamera () {
    if (this.post && !this.stopped) {
      this.post({ op: 'camera' })
    }
  }

  setTorch (on) {
    if (this.post && !this.stopped) {
      this.post({ op: 'torch', on: !!on })
    }
  }

  stop () {
    this.stopped = true
    if (this.frame) {
      cancelAnimationFrame(this.frame)
      this.frame = null
    }
    window.removeEventListener('johkuhubscanner', this.onEvent)
    this.settle()
    if (this.post) {
      this.post({ op: 'stop' })
    }
  }
}
